export const SHANI_TRANSIT_HEADLINE = "Shani Transit — Live Saturn Gochar";
import type { ActiveTransit, SadeSatiInfo } from "@/lib/api/transits";

export type SadeSatiPhase = SadeSatiInfo["phase"] | "none";

/**
 * Each Saturn status a Moon-sign result shows: the three Sade Sati phases,
 * "none", and "dhaiya" (Saturn 4th or 8th from the Moon sign, the engine's
 * effect "dhaiya" in transits.yaml), which the /shani-transit/ picker
 * (2026-10-20) and the /sade-sati/ picker (2026-10-21) label apart from
 * "NOT ACTIVE".
 */
export const SATURN_STATUS_STYLES: Record<
  SadeSatiPhase | "dhaiya",
  { bg: string; border: string; text: string; badge: string; badgeBg: string }
> = {
  rising: {
    bg: "var(--color-warning-bg)",
    border: "var(--color-warning-tint)",
    text: "var(--color-warning-text-dark)",
    badge: "var(--color-warning-text)",
    badgeBg: "var(--color-warning-bg)",
  },
  peak: {
    bg: "var(--color-error-bg)",
    border: "var(--color-error-tint)",
    text: "var(--color-error-text)",
    badge: "var(--color-error-text-dark)",
    badgeBg: "var(--color-error-bg)",
  },
  setting: {
    bg: "var(--color-warning-bg)",
    border: "var(--color-warning-tint)",
    text: "var(--color-warning-orange-dark)",
    badge: "var(--color-warning-orange-dark)",
    badgeBg: "var(--color-warning-bg)",
  },
  none: {
    bg: "var(--color-success-bg)",
    border: "var(--color-border-valid)",
    text: "var(--color-success-text-dark)",
    badge: "var(--color-success-text-dark)",
    badgeBg: "var(--color-success-bg)",
  },
  dhaiya: {
    bg: "var(--color-surface-warm)",
    border: "var(--color-warning-tint)",
    text: "var(--color-warning-text-dark)",
    badge: "var(--color-warning-text-dark)",
    badgeBg: "var(--color-warning-bg)",
  },
};

/**
 * The Dhaiya positions: Saturn 4th (Kantaka Shani) or 8th (Ashtama Shani) from
 * the Moon sign. `label` is the /shani-transit/ badge; `short` is the
 * /sade-sati/ badge, whose slot is narrower (no reserve, no shrink-0): measured
 * 2026-10-21 in Chromium at 320, 360, 390, 412 and 640 px on the system font,
 * "DHAIYA · KANTAKA (4th)" wraps the badge row to two lines at 360 and 390 for
 * Sagittarius, Capricorn and Scorpio where "NOT ACTIVE" stays on one, while
 * "DHAIYA (4th)" / "DHAIYA (8th)" is never taller than "SETTING PHASE" or
 * "RISING PHASE" for any sign at any of those widths.
 */
const DHAIYA_LABELS: Readonly<Record<number, { label: string; short: string; name: string }>> = {
  4: { label: "DHAIYA · KANTAKA (4th)", short: "DHAIYA (4th)", name: "Kantaka Shani" },
  8: { label: "DHAIYA · ASHTAMA (8th)", short: "DHAIYA (8th)", name: "Ashtama Shani" },
};

export interface DhaiyaBadge {
  label: string;
  short: string;
  /** "Kantaka Shani" (4th) or "Ashtama Shani" (8th). */
  name: string;
  house: 4 | 8;
}

/**
 * The Dhaiya badge for a picked Moon sign: only when the engine itself labels
 * Saturn's effect "dhaiya" (transits.yaml, houses 4 and 8) and its house
 * agrees, so the badge never contradicts the engine's governed house reading.
 * `saturn` must be Saturn's entry in the reply for that same Moon sign.
 */
export function dhaiyaBadge(saturn: ActiveTransit | null | undefined): DhaiyaBadge | null {
  if (saturn?.effect !== "dhaiya") return null;
  const house = saturn.house_from_moon;
  const entry = DHAIYA_LABELS[house];
  if (!entry || (house !== 4 && house !== 8)) return null;
  return { label: entry.label, short: entry.short, name: entry.name, house };
}

/** "1st", "2nd", "3rd", "4th", "11th", "12th". */
export function houseOrdinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}
